import { useState, useEffect } from 'react';
import { handoverService } from '../../services/api';

/**
 * Custom hook to fetch handovers for a set of rentals
 * @param {Array} rentals - Array of rental objects
 * @returns {Object} Object containing handovers data and loading state
 */
export const useHandoversForRentals = (rentals) => {
  const [handovers, setHandovers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHandovers = async () => {
      if (!rentals || rentals.length === 0) {
        setHandovers({});
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Create a map to store handovers by rental ID
        const handoverMap = {};
        
        // Fetch handovers for each rental in parallel
        await Promise.all(
          rentals.map(async (rental) => {
            if (rental._id) {
              try {
                const response = await handoverService.getHandoversByRental(rental._id);
                if (response && response.data) {
                  handoverMap[rental._id] = response.data;
                }
              } catch (err) {
                console.error(`Error fetching handovers for rental ${rental._id}:`, err);
              }
            }
          })
        );
        
        setHandovers(handoverMap);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching handovers:', err);
        setError(err);
        setLoading(false);
      }
    };

    fetchHandovers();
  }, [rentals]);

  return { handovers, loading, error };
};

/**
 * Get handover information for display
 * @param {Object} handover - Handover object
 * @returns {Object} Formatted handover data for display
 */
export const formatHandoverForDisplay = (handover) => {
  if (!handover) return null;
  
  const handoverDate = new Date(handover.handoverDate || Date.now());
  
  return {
    id: handover._id,
    type: handover.handoverType,
    date: handoverDate,
    formattedDate: handoverDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    status: handover.status,
    mileage: handover.mileage,
    fuelLevel: handover.fuelLevel,
    condition: handover.condition,
    vehicleInstanceId: handover.vehicleInstanceId,
    employee: handover.employee?.firstName 
      ? `${handover.employee.firstName} ${handover.employee.lastName}`
      : 'Employee',
    photos: handover.photos || [],
    videos: handover.videos || [],
    voiceNotes: handover.voiceNotes || [],
    damages: handover.damages || [],
    notes: handover.notes || '',
    signatures: handover.signatures || {},
    customer: handover.customer || {},
    vehicle: handover.vehicle || {}
  };
};

/**
 * Check if a handover is on a specific day
 * @param {Date} day - The day to check
 * @param {Object} handover - Handover object
 * @returns {boolean} True if handover is on the specified day
 */
export const isHandoverOnDay = (day, handover) => {
  if (!handover || !day) return false;
  
  const handoverDate = new Date(handover.handoverDate);
  return day.getDate() === handoverDate.getDate() &&
         day.getMonth() === handoverDate.getMonth() &&
         day.getFullYear() === handoverDate.getFullYear();
};

/**
 * Get color for handover based on type and status
 * @param {Object} handover - Handover object
 * @returns {string} CSS classes for coloring
 */
export const getHandoverColor = (handover) => {
  if (!handover) return '';
  
  if (handover.status === 'pending') {
    return 'bg-yellow-300 text-yellow-800';
  }
  
  if (handover.handoverType === 'pickup') {
    return 'bg-teal-500 text-white';
  } else if (handover.handoverType === 'return') {
    return 'bg-purple-500 text-white';
  }
  
  return 'bg-gray-500 text-white';
};
